/**
 * Sellox POS - Offline Sync Queue
 * 
 * Intercepts failed API requests (POST/PUT/DELETE) when offline,
 * saves them to IndexedDB, and replays them when network returns.
 * This enables ALL features to work offline seamlessly.
 */
import axios from 'axios';
import { scopedKey } from './cacheScope';

const DB_NAME = 'sellox_sync_db';
const DB_VERSION = 1;
const STORE_NAME = 'sync_queue';

let syncDb = null;

function openSyncDB() {
  return new Promise((resolve, reject) => {
    if (syncDb) { resolve(syncDb); return; }
    if (!window.indexedDB) { reject(new Error('No IndexedDB')); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = (e) => { syncDb = e.target.result; resolve(syncDb); };
    req.onerror = (e) => reject(e.target.error);
  });
}

// Add a failed request to the sync queue
export async function queueRequest(method, url, data, headers) {
  try {
    const db = await openSyncDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      // 🛡️ Preserve idempotency + device headers so retries get the SAME
      // X-Idempotency-Key (otherwise the server creates 50 dups on replay).
      const preservedHeaders = {
        'Content-Type': headers?.['Content-Type'] || 'application/json',
      };
      const idemKey = headers?.['X-Idempotency-Key'] || headers?.['x-idempotency-key'];
      if (idemKey) preservedHeaders['X-Idempotency-Key'] = idemKey;
      const devId = headers?.['X-Sellox-Device-Id'] || headers?.['x-sellox-device-id'];
      if (devId) preservedHeaders['X-Sellox-Device-Id'] = devId;
      store.add({
        method,
        url,
        data,
        headers: preservedHeaders,
        timestamp: new Date().toISOString(),
        retries: 0,
      });
      tx.oncomplete = () => resolve(true);
      tx.onerror = (e) => reject(e.target.error);
    });
  } catch { return false; }
}

// Get all pending requests
export async function getPendingRequests() {
  try {
    const db = await openSyncDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => resolve([]);
    });
  } catch { return []; }
}

// Remove a synced request
export async function removeRequest(id) {
  try {
    const db = await openSyncDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.delete(id);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    });
  } catch { return false; }
}

// Clear entire queue
export async function clearQueue() {
  try {
    const db = await openSyncDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).clear();
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    });
  } catch { return false; }
}

// Get queue count
export async function getQueueCount() {
  const items = await getPendingRequests();
  return items.length;
}

// Replay all queued requests (called when back online)
let isSyncing = false;
export async function replayQueue(onProgress) {
  if (isSyncing) return { synced: 0, failed: 0 };
  isSyncing = true;
  const items = await getPendingRequests();
  let synced = 0, failed = 0;

  for (const item of items) {
    try {
      const config = {
        method: item.method,
        url: item.url,
        headers: { ...item.headers, Authorization: axios.defaults.headers.common?.['Authorization'] || '' },
      };
      if (item.data && ['post', 'put', 'patch'].includes(item.method)) {
        config.data = item.data;
      }
      await axios(config);
      await removeRequest(item.id);
      synced++;
      if (onProgress) onProgress({ synced, failed, total: items.length });
    } catch (e) {
      // If server explicitly rejects (4xx), archive + remove from queue (don't retry)
      if (e.response && e.response.status >= 400 && e.response.status < 500) {
        try {
          const arch = JSON.parse(localStorage.getItem('sellox_rejected_ops') || '[]');
          arch.push({ method: item.method, url: item.url, data: item.data, timestamp: item.timestamp, rejected_at: new Date().toISOString(), reason: e.response?.data?.detail || `HTTP ${e.response.status}` });
          localStorage.setItem('sellox_rejected_ops', JSON.stringify(arch.slice(-100)));
        } catch (_) {}
        await removeRequest(item.id);
        failed++;
      } else {
        // Network still down or server error - stop syncing
        failed++;
        break;
      }
    }
  }

  isSyncing = false;
  return { synced, failed, total: items.length };
}

// URLs that should NOT be queued (read-only or auth)
const SKIP_QUEUE_PATTERNS = [
  '/api/auth/',
  '/api/init-data',
  '/api/exchange-rates',
];

function shouldQueue(url) {
  return !SKIP_QUEUE_PATTERNS.some(p => url.includes(p));
}

// ─── Offline READ cache: every successful GET is cached; when offline the ───
// ─── cached copy is served so every page keeps working without network.  ───
const READ_DB = 'sellox_readcache_db';
let readDb = null;

function openReadDB() {
  return new Promise((resolve, reject) => {
    if (readDb) { resolve(readDb); return; }
    const req = indexedDB.open(READ_DB, 1);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains('gets')) db.createObjectStore('gets', { keyPath: 'url' });
    };
    req.onsuccess = (e) => { readDb = e.target.result; resolve(readDb); };
    req.onerror = (e) => reject(e.target.error);
  });
}

async function cacheGet(url, data) {
  try {
    const db = await openReadDB();
    const tx = db.transaction('gets', 'readwrite');
    tx.objectStore('gets').put({ url, data, cached_at: new Date().toISOString() });
  } catch { /* ignore */ }
}

async function loadCachedGet(url) {
  try {
    const db = await openReadDB();
    return new Promise((resolve) => {
      const req = db.transaction('gets', 'readonly').objectStore('gets').get(url);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch { return null; }
}

const READ_SKIP = ['/api/auth/', '/api/videos', '/public-video'];
// Key MUST include params: "/api/products" with search=123 is not the same page as the full list
// (La Leyenda 2026-10-01: offline search showed all 2624 products).
function readKey(cfg) {
  const p = cfg.params && typeof cfg.params === 'object' ? Object.keys(cfg.params).sort().map(k => `${k}=${cfg.params[k]}`).join('&') : '';
  return scopedKey(cfg, (cfg.url || '') + (p ? `?${p}` : ''));
}

export function installOfflineReadCache() {
  axios.interceptors.response.use(
    (response) => {
      const cfg = response.config || {};
      const url = cfg.url || '';
      if (cfg.method === 'get' && url.includes('/api/') && !READ_SKIP.some(p => url.includes(p))
          && response.data && typeof response.data === 'object') {
        try {
          const size = JSON.stringify(response.data).length;
          if (size < 3000000) cacheGet(readKey(cfg), response.data);
        } catch { /* ignore */ }
      }
      return response;
    },
    async (error) => {
      const cfg = error.config || {};
      if (cfg.method === 'get' && !error.response && (cfg.url || '').includes('/api/')) {
        const hit = await loadCachedGet(readKey(cfg));
        if (hit) {
          const data = hit.data;
          if (data && typeof data === 'object' && !Array.isArray(data)) data._offline_cache = true;
          return { data, status: 200, statusText: 'Offline Cache', config: cfg, headers: { 'x-offline-cache': hit.cached_at } };
        }
      }
      return Promise.reject(error);
    }
  );
}

// Install axios interceptor for offline queueing
export function installOfflineInterceptor(showToast) {
  // 🛡️ Idempotency: every queued WRITE gets a stable UUID header. The
  // backend uses this as a dedup key so the same request can be replayed
  // (after reconnect / retry / background sync) without creating duplicate
  // sales / wholesale orders. Combined with server-side Layer 1 (60s) +
  // Layer 3 (24h fingerprint) protections this stops the FLOR-style robot
  // pattern even if the client re-queues by mistake.
  axios.interceptors.request.use((config) => {
    const m = (config.method || '').toLowerCase();
    // Persistent device id (per browser install) — sent on EVERY request so the desktop vision agent
    // picks up the session token from the first GET after login (2026-10-01: it only rode on writes,
    // so a store that hadn't sold yet had its cameras agent sitting idle all morning).
    let did = '';
    try { did = localStorage.getItem('sellox_device_id') || ''; } catch {}
    if (!did) {
      did = (crypto && crypto.randomUUID) ? crypto.randomUUID().slice(0, 16) : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      try { localStorage.setItem('sellox_device_id', did); } catch {}
    }
    config.headers = config.headers || {};
    if (!config.headers['X-Sellox-Device-Id']) config.headers['X-Sellox-Device-Id'] = did;
    if (['post', 'put', 'patch', 'delete'].includes(m) && shouldQueue(config.url || '')) {
      if (!config.headers['X-Idempotency-Key']) {
        const uuid = (crypto && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
        config.headers['X-Idempotency-Key'] = uuid;
      }
    }
    return config;
  });

  axios.interceptors.response.use(
    response => response,
    async error => {
      const config = error.config;
      // Only queue write requests (POST/PUT/DELETE) that failed due to network
      if (
        config &&
        !error.response &&
        !navigator.onLine &&
        ['post', 'put', 'delete', 'patch'].includes(config.method) &&
        shouldQueue(config.url)
      ) {
        // Parse request data for fake response
        let reqData = {};
        try { reqData = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {}); } catch {}

        // Inject idempotency key so server-side replay can dedupe (POST creates)
        let payload = config.data;
        if (config.method === 'post' && reqData && typeof reqData === 'object' && !Array.isArray(reqData) && !reqData.client_op_id) {
          reqData.client_op_id = `op_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
          payload = JSON.stringify(reqData);
        }

        const queued = await queueRequest(config.method, config.url, payload, config.headers);
        if (queued && showToast) {
          showToast('已保存到本地，恢复网络后自动同步');
        }
        // Return fake success with the request data so UI can update
        const fakeResponse = {
          ...reqData,
          _offline: true,
          _queued: true,
          id: reqData.id || `offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        };
        return {
          data: fakeResponse,
          status: 200,
          statusText: 'Queued Offline',
          config,
        };
      }
      return Promise.reject(error);
    }
  );
}
